(function () {
  'use strict';

  // ==========================================================================
  // CC PIPELINE BRIDGE — runs on activated products only
  // ==========================================================================
  // Keeps the Atelier pickers and Pipeline's native variant radios in agreement.
  // Pipeline stays the single source of truth for variant id and price; this
  // deliberately does NOT port Atelier's cc-variant-switcher.js.
  //
  //   <count axis> (native radios)   ->  initial picker slot count
  //   <count axis> (native radios)   ->  text-engraving count-first slot count
  //   <count axis> (native radios)   ->  birthstone picker slot count
  //   <count axis> (native radios)   ->  birthflower picker slot count
  //   Atelier upgrade toggle         ->  <upgrade axis> radios
  //   <upgrade axis> (native radios) ->  Atelier upgrade toggle
  //
  // Axis names are per-product and are NEVER hardcoded — they are derived from
  // block_config + the product's own option list. Across the catalogue the count
  // axis appears as "Select Number of Initials", "Initials & Symbols", "Number
  // of Initials", "Number of Heart Pendants", "Number of Names", "Number of
  // Tags", "Number of Hearts", or is absent entirely.
  // ==========================================================================

  var pickers = document.querySelector('[data-cc-atelier-pickers]');
  var form = document.querySelector('form[data-cc-pilot]');
  if (!pickers || !form) return;

  var dataEl = document.querySelector('[data-cc-pipeline-variants]');
  if (!dataEl) return;

  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var cfg = data.block_config || {};
  var options = data.options || [];              // [{name, values:[...]}, ...]
  var optionNames = options.map(function (o) { return o.name; });

  var picker = pickers.querySelector('[data-cc-initial-picker]');
  var engraving = pickers.querySelector('[data-cc-text-engraving]');
  var birthstones = pickers.querySelectorAll('[data-cc-birthstone-picker]');
  // A product may declare two independent groups (birthstone_picker and
  // birthstone_picker_2). `birthstone` stays the first for the legacy single-group
  // paths; gating and count-driving below walk every root.
  var birthstone = birthstones[0] || null;
  var birthflower = pickers.querySelector('[data-cc-birthflower-picker]');
  // Zodiac is single-select, the date field is one input, and text_fields is a
  // fixed array from block_config — none of them has a count selector and none
  // moves the variant or the price, so none gets an axis. They contribute a
  // completeness gate only.
  var zodiac = pickers.querySelector('[data-cc-zodiac-picker]');
  var dateField = pickers.querySelector('[data-cc-date-field]');
  var textFields = pickers.querySelector('[data-cc-text-fields]');
  var photo = pickers.querySelector('[data-cc-photo-upload]');
  var upgradeToggle = pickers.querySelector('[data-cc-upgrade-toggle]');
  var atc = form.querySelector('[data-add-to-cart]');
  var idInput = form.querySelector('input[name="id"]');

  var syncing = false;

  function warn(msg) {
    if (window.console && console.warn) console.warn('[cc-atelier] ' + msg);
  }

  function intOr(v, fallback) {
    var n = parseInt(v, 10);
    return (isNaN(n) || n < 0) ? fallback : n;
  }

  // --- axis detection -------------------------------------------------------
  // Shared by the initial picker and the count-first engraving block.
  //   1. explicit variant_option_name from block_config
  //   2. a strictly-numeric option whose values run from `min` up to at most `max`
  // The range check is what keeps "Ring Size" (5,6,7,8,9,10) from being mistaken
  // for a count axis on the initial ring, whose picker max is 1.
  function findAxisIndex(explicitName, min, max, who) {
    if (explicitName) {
      var explicit = optionNames.indexOf(explicitName);
      if (explicit < 0) warn(who + '.variant_option_name "' + explicitName + '" matches no product option');
      return explicit;
    }
    if (max <= 1) return -1;
    for (var i = 0; i < options.length; i++) {
      var vals = options[i].values || [];
      if (vals.length < 2) continue;
      var nums = [], ok = true;
      for (var j = 0; j < vals.length; j++) {
        var raw = String(vals[j]).trim();
        if (!/^\d+$/.test(raw)) { ok = false; break; }
        nums.push(parseInt(raw, 10));
      }
      if (!ok) continue;
      nums.sort(function (a, b) { return a - b; });
      if (nums[0] !== min) continue;
      if (nums[nums.length - 1] > max) continue;
      return i;
    }
    return -1;
  }

  // ---- initial picker axis ----
  var countIdx = -1;
  if (picker && cfg.initial_picker) {
    var ip = cfg.initial_picker;
    countIdx = findAxisIndex(ip.variant_option_name, intOr(ip.min, 1) || 1, intOr(ip.max, 1) || 1, 'initial_picker');
  }
  var countOption = countIdx > -1 ? optionNames[countIdx] : null;

  // ---- engraving axis ----
  // data-count-first is set by the Liquid block only when multi_slot +
  // count_from_variant + a variant_option_name that actually resolved, so the
  // DOM is the authoritative signal for whether this is count-driven at all.
  var engravingCountFirst = !!(engraving && engraving.hasAttribute('data-count-first'));
  var engravingOption = null;
  if (engravingCountFirst) {
    var te = cfg.text_engraving || {};
    var eIdx = findAxisIndex(te.variant_option_name, intOr(te.min_slots, 1) || 1, intOr(te.max_slots, 5) || 5, 'text_engraving');
    engravingOption = eIdx > -1 ? optionNames[eIdx] : null;
  }

  // ---- birthstone axis ----
  // The birthstone block is metaobject-driven and, for multi-stone products,
  // count-driven exactly like the initial picker: block_config carries min/max
  // and (when the count is a variant axis) count_from_variant +
  // variant_option_name, e.g. "Number of Birthstones". Unlike text_engraving the
  // ported snippet emits no data-count-first flag, so the config is the only
  // signal — hence the same findAxisIndex() detector, with an explicit
  // count_from_variant:false opting out. Fixed-count products (min == max) render
  // no count selector and findAxisIndex returns -1 for max <= 1, so nothing is
  // driven and nothing is hidden.
  // One entry per rendered group: { root, option }. The root carries
  // data-group-key so each group is matched to ITS OWN config block and axis —
  // two groups on one product have two different "how many" options.
  var birthstoneGroups = [];
  for (var _bi = 0; _bi < birthstones.length; _bi++) {
    var _root = birthstones[_bi];
    var _key = _root.getAttribute('data-group-key') || 'birthstone_picker';
    var _bp = cfg[_key];
    var _opt = null;
    if (_bp && _bp.count_from_variant !== false) {
      var _idx = findAxisIndex(
        _bp.variant_option_name,
        intOr(_bp.min, 1) || 1,
        intOr(_bp.max, 5) || 5,
        _key
      );
      if (_idx > -1) _opt = optionNames[_idx];
    }
    birthstoneGroups.push({ root: _root, option: _opt });
  }
  var birthstoneOption = birthstoneGroups.length ? birthstoneGroups[0].option : null;

  // ---- birthflower axis ----
  // Same shape as the birthstone axis: metaobject-driven block, count-driven for
  // multi-flower products via min/max + count_from_variant + variant_option_name
  // ("Number of Flowers" in the wild). Single-flower products (max 1) render no
  // count selector and findAxisIndex returns -1 for max <= 1, so nothing drives
  // and nothing is hidden.
  var birthflowerCountIdx = -1;
  if (birthflower && cfg.birthflower_picker) {
    var bf = cfg.birthflower_picker;
    if (bf.count_from_variant !== false) {
      birthflowerCountIdx = findAxisIndex(
        bf.variant_option_name,
        intOr(bf.min, 1) || 1,
        intOr(bf.max, 1) || 1,
        'birthflower_picker'
      );
    }
  }
  var birthflowerOption = birthflowerCountIdx > -1 ? optionNames[birthflowerCountIdx] : null;

  // ---- upgrade axis ----
  function detectUpgradeIndex() {
    var ut = cfg.upgrade_toggle;
    if (!ut) return -1;
    if (ut.variant_option_name) return optionNames.indexOf(ut.variant_option_name);
    var byLabel = optionNames.indexOf(ut.label);
    if (byLabel > -1) return byLabel;
    var wanted = (ut.options || ['No', 'Yes']).map(String).sort().join('|');
    for (var i = 0; i < options.length; i++) {
      var vals = (options[i].values || []).map(String).sort().join('|');
      if (vals === wanted) return i;
    }
    return -1;
  }
  var upgradeIdx = detectUpgradeIndex();
  var upgradeOption = upgradeIdx > -1 ? optionNames[upgradeIdx] : null;
  if (cfg.upgrade_toggle && upgradeIdx < 0) {
    warn('upgrade_toggle is configured but no matching variant option was found — the toggle will write its property but cannot move the variant/price.');
  }

  // --- unpriced-count guard (initial picker only) ---------------------------
  // If the picker offers several initials but NO variant axis prices the count,
  // every count costs the same, which on a copied block_config silently
  // under-charges. Clamp to `min` unless explicitly opted out.
  // NOT applied to text_engraving: its non-count-first multi-slot mode ("+ Add
  // another", bounded by max_slots) is a deliberate Atelier pattern rather than
  // a misconfiguration, so unpriced extra slots there are reported by the
  // anomaly sweep instead of being silently changed.
  var clampToMin = false;
  if (picker && cfg.initial_picker) {
    var ipMax = intOr(cfg.initial_picker.max, 1) || 1;
    var ipMin = intOr(cfg.initial_picker.min, 1) || 1;
    if (ipMax > 1 && countIdx < 0 && cfg.initial_picker.allow_unpriced_count !== true) {
      clampToMin = true;
      warn('initial_picker.max=' + ipMax + ' but no variant option prices the count. Clamping to ' + ipMin + '.');
    }
  }

  // --- native radio helpers -------------------------------------------------
  // The radios live in the variant-selectors block, OUTSIDE <form>, bound to it
  // via the form="" attribute — so query the document, not the form.
  function radios(optionName) {
    return document.querySelectorAll('input[name="options[' + optionName + ']"]');
  }
  function selectedValue(optionName) {
    var checked = document.querySelector('input[name="options[' + optionName + ']"]:checked');
    return checked ? checked.value : null;
  }
  function setNativeOption(optionName, value) {
    var list = radios(optionName);
    for (var i = 0; i < list.length; i++) {
      if (list[i].value !== value) continue;
      if (!list[i].checked) {
        list[i].checked = true;
        list[i].dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }
    return false;
  }

  // --- completeness gates ---------------------------------------------------
  // Eight independent gates; add-to-cart needs all of them. Each defaults to
  // satisfied when its block is absent, so a notes-only product is never gated.
  var gateInitials = true;
  var gateEngraving = true;
  var gateBirthstones = true;
  var gateBirthflowers = true;
  var gateZodiac = true;
  var gateDate = true;
  var gateTextFields = true;
  var gatePhoto = true;

  // The picker scripts emit their first change event BEFORE this script runs
  // (script order), so read the DOM for the starting state rather than waiting
  // for an event that has already fired.
  function readInitialsFromDom() {
    if (!picker) return true;
    var slots = picker.querySelectorAll('[data-cc-slots] button');
    if (!slots.length) return false;
    for (var i = 0; i < slots.length; i++) {
      if (!slots[i].textContent.trim()) return false;
    }
    return true;
  }

  function readEngravingFromDom() {
    if (!engraving) return true;
    var isRequired = engraving.hasAttribute('data-required');
    // Count-first always gates on all-N-filled (the block emits required:true).
    if (!isRequired && !engravingCountFirst) return true;
    var inputs = engraving.querySelectorAll('[data-cc-slots] input, [data-cc-engraving-input]');
    if (!inputs.length) return false; // count-first with no count chosen yet
    var filled = 0;
    for (var i = 0; i < inputs.length; i++) {
      if (String(inputs[i].value).trim() !== '') filled++;
    }
    var min = engravingCountFirst
      ? inputs.length                                   // every box required
      : (intOr(engraving.dataset.minSlots, 1) || 1);
    return filled >= min;
  }

  // A stone slot is complete only once it carries a month. The picker marks a
  // filled slot with --filled, so counting that class is the DOM equivalent of
  // the "count === filled stones" rule the JS emits. Zero slots means no count
  // has been chosen yet (range mode), which is incomplete. data-cc-conditional
  // is never emitted by the ported snippet, but the branch is kept so a
  // conditional stone product would gate on Yes and pass freely on No.
  function readOneBirthstoneGroup(root) {
    if (!root) return true;
    if (root.hasAttribute('data-cc-conditional')) {
      var yes = root.querySelector('[data-cc-bs-toggle="Yes"]');
      if (!yes || !yes.classList.contains('is-selected')) return true;
    }
    var slots = root.querySelectorAll('[data-cc-slots] button');
    if (!slots.length) return false;
    for (var i = 0; i < slots.length; i++) {
      if (!slots[i].classList.contains('cc-birthstone-picker__slot--filled')) return false;
    }
    return true;
  }

  // EVERY group must be complete — with two groups the event detail only
  // describes whichever one just changed, so the DOM is the source of truth.
  function firstFailingBirthstoneGroup() {
    for (var i = 0; i < birthstones.length; i++) {
      if (!readOneBirthstoneGroup(birthstones[i])) return birthstones[i];
    }
    return null;
  }

  function readBirthstonesFromDom() {
    return firstFailingBirthstoneGroup() === null;
  }

  // The birthflower block wears the birthstone picker's CSS classes (deliberate
  // reuse — see cc-block-birthflower-picker.liquid), so a filled slot is marked
  // with the SAME --filled class. Only the root attribute differs, which is what
  // scopes this read to the flower block.
  function readBirthflowersFromDom() {
    if (!birthflower) return true;
    var slots = birthflower.querySelectorAll('[data-cc-slots] button');
    if (!slots.length) return false;
    for (var i = 0; i < slots.length; i++) {
      if (!slots[i].classList.contains('cc-birthstone-picker__slot--filled')) return false;
    }
    return true;
  }

  // Zodiac wears the birthstone slot classes too, so a chosen sign is the same
  // --filled marker. Only gates when the block is marked required.
  function readZodiacFromDom() {
    if (!zodiac) return true;
    if (!zodiac.hasAttribute('data-required')) return true;
    var slot = zodiac.querySelector('[data-cc-slots] button');
    return !!(slot && slot.classList.contains('cc-birthstone-picker__slot--filled'));
  }

  // The date block only writes its hidden output once the typed value parses to
  // a real calendar date, so a non-empty output IS the "valid and complete"
  // signal — no need to re-implement the validation here.
  function readDateFromDom() {
    if (!dateField) return true;
    if (!dateField.hasAttribute('data-required')) return true;
    var out = dateField.querySelector('[data-cc-date-output]');
    return !!(out && String(out.value).trim() !== '');
  }

  // Each text field carries its own data-required, so the block is complete
  // when every required input holds something other than whitespace. Optional
  // fields never gate. Mirrors the aggregated cc:textfields-changed contract.
  function readTextFieldsFromDom() {
    if (!textFields) return true;
    var required = textFields.querySelectorAll('[data-cc-text-field][data-required]');
    for (var i = 0; i < required.length; i++) {
      var input = required[i].querySelector('[data-cc-text-field-input]');
      if (!input || String(input.value).trim() === '') return false;
    }
    return true;
  }

  // The file input is the source of truth: a required photo block is complete
  // once the input holds a file. Nothing is uploaded at this point -- the upload
  // happens on submit, in the scoped path further down.
  function readPhotoFromDom() {
    if (!photo) return true;
    if (!photo.hasAttribute('data-required')) return true;
    var fileInput = photo.querySelector('[data-cc-photo-input]');
    return !!(fileInput && fileInput.files && fileInput.files.length);
  }

  gateInitials = readInitialsFromDom();
  gateEngraving = readEngravingFromDom();
  gateBirthstones = readBirthstonesFromDom();
  gateBirthflowers = readBirthflowersFromDom();
  gateZodiac = readZodiacFromDom();
  gateDate = readDateFromDom();
  gateTextFields = readTextFieldsFromDom();
  gatePhoto = readPhotoFromDom();

  var hasGate = !!(picker || engraving || birthstone || birthflower || zodiac || dateField || textFields || photo);

  function allGatesPass() {
    return gateInitials && gateEngraving && gateBirthstones
      && gateBirthflowers && gateZodiac && gateDate && gateTextFields && gatePhoto;
  }

  function applyGate() {
    if (!atc || !hasGate) return;
    if (!allGatesPass()) {
      atc.disabled = true;
      atc.setAttribute('data-cc-gated', '');
    } else if (atc.hasAttribute('data-cc-gated')) {
      atc.disabled = false;
      atc.removeAttribute('data-cc-gated');
    }
  }

  if (picker) {
    document.addEventListener('cc:initials-changed', function (e) {
      gateInitials = !!(e.detail && e.detail.count > 0);
      applyGate();
    });
  }

  if (engraving) {
    document.addEventListener('cc:engraving-changed', function (e) {
      var d = e.detail || {};
      // Atelier only gates on engraving when the block is marked required;
      // count-first emits required:true itself.
      if (d.required !== true) { gateEngraving = true; }
      else { gateEngraving = (d.filled || 0) >= (d.min || 1); }
      applyGate();
    });
  }

  if (birthstone) {
    // The picker's LOCKED contract emits count 0 while ANY slot is still empty
    // and the full N only when every slot holds a month, so `count >= min` is
    // the complete test. min is read from the detail rather than defaulted to 1
    // because conditional mode expresses "not required" as min 0.
    document.addEventListener('cc:birthstones-changed', function (e) {
      var d = e.detail || {};
      var min = typeof d.min === 'number' ? d.min : 1;
      // Single group keeps the LOCKED count>=min contract. With two groups the
      // detail only describes the one that changed, so fall back to the DOM.
      gateBirthstones = birthstones.length > 1
        ? readBirthstonesFromDom()
        : (d.count || 0) >= min;
      applyGate();
    });
  }

  if (birthflower) {
    // Identical contract to the stone picker: count is 0 until every slot holds
    // a month, then the full N.
    document.addEventListener('cc:birthflowers-changed', function (e) {
      var d = e.detail || {};
      var min = typeof d.min === 'number' ? d.min : 1;
      gateBirthflowers = (d.count || 0) >= min;
      applyGate();
    });
  }

  if (zodiac) {
    // Single-select: the picker reports filled 1/0 and required from its own
    // config, so an optional zodiac block never blocks add-to-cart.
    document.addEventListener('cc:zodiac-changed', function (e) {
      var d = e.detail || {};
      gateZodiac = d.required !== true ? true : (d.filled || 0) >= (d.min || 1);
      applyGate();
    });
  }

  if (dateField) {
    // filled is 1 only for a fully parsed, in-range calendar date.
    document.addEventListener('cc:date-changed', function (e) {
      var d = e.detail || {};
      gateDate = d.required !== true ? true : (d.filled || 0) >= (d.min || 1);
      applyGate();
    });
  }

  if (textFields) {
    // The block reports `complete` for the whole array (all required entries
    // filled). A product with only optional fields reports complete:true from
    // the first emit, so it never blocks add-to-cart.
    document.addEventListener('cc:textfields-changed', function (e) {
      var d = e.detail || {};
      gateTextFields = d.complete !== false;
      applyGate();
    });
  }

  if (photo) {
    // filled is 1 only once a file has passed the block's own type/size/
    // dimension validation, so a rejected file never satisfies the gate.
    document.addEventListener('cc:photo-changed', function (e) {
      var d = e.detail || {};
      gatePhoto = d.required !== true ? true : (d.filled || 0) >= (d.min || 1);
      applyGate();
    });
  }

  if (hasGate) {
    document.addEventListener('change', function () { setTimeout(applyGate, 0); }, true);

    // Hard stop: never let an incomplete selection reach /cart/add.
    form.addEventListener('submit', function (e) {
      if (allGatesPass()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var focusEl = (!gateInitials && picker)
        ? picker.querySelector('[data-cc-letter-grid]')
        : (!gateEngraving && engraving)
          ? engraving.querySelector('[data-cc-slots] input, [data-cc-engraving-input]')
          : (!gateBirthstones && firstFailingBirthstoneGroup())
            ? firstFailingBirthstoneGroup().querySelector('[data-cc-slots], [data-cc-stone-panel]')
            : (!gateBirthflowers && birthflower)
              ? birthflower.querySelector('[data-cc-slots], [data-cc-flower-panel]')
              : (!gateZodiac && zodiac)
                ? zodiac.querySelector('[data-cc-slots], [data-cc-sign-panel]')
                : (!gateDate && dateField)
                  ? dateField.querySelector('[data-cc-date-text]')
                  : (!gateTextFields && textFields)
                    ? textFields.querySelector('[data-cc-text-field][data-required] [data-cc-text-field-input]')
                    : (photo && photo.querySelector('[data-cc-zone]'));
      if (focusEl) focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
  }

  // --- photo upload submit path ---------------------------------------------
  // SCOPED to products that actually render a photo block. Everything else keeps
  // Pipeline's own add-to-cart untouched.
  //
  // Why this exists: <product-form> runs Alpine's productAddButtonForm(), which
  // serialises the form to a string before POSTing, so a File value reaches
  // Shopify as the literal text "[object File]" and no file is ever uploaded.
  // Shopify itself accepts a native multipart file line-item property fine, and
  // Pipeline's form already carries enctype="multipart/form-data" -- only the
  // AJAX serialisation is lossy. So on these products we take the submit
  // ourselves and POST a real FormData.
  //
  // The intercept is on the BUTTON in the capture phase, not on form submit: a
  // click on a type=submit button runs click handlers first and the implicit
  // submission after, so preventDefault here stops both Alpine's handler and the
  // form submission. Capturing on the form's submit event would be too late if
  // Alpine is bound to the click.
  //
  // This composes with the positional-button fix rather than fighting it: that
  // fix is about WHICH button Pipeline binds to (the first <button> inside
  // <product-form>, which is why the pickers render after .product__submit).
  // Here we bind to the same real add-to-cart button Pipeline resolved, and stop
  // the event before Pipeline's own handler sees it -- so exactly one add is
  // issued, by us, and no phantom line can be created.
  function photoHasFile() {
    if (!photo) return false;
    var fileInput = photo.querySelector('[data-cc-photo-input]');
    return !!(fileInput && fileInput.files && fileInput.files.length);
  }

  var photoBusy = false;

  function announceCart(cart, addedItem) {
    // Exactly the events Pipeline's own add-to-cart dispatches on document, with
    // the same detail shapes, so the count, popdown and drawer refresh the way
    // they do for every other product:
    //   theme:cart:change  { detail: { cart } }
    //   theme:cart:popdown { detail: { variant } }
    //   theme:cart:reload  { bubbles: true }
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('theme:cart:popdown', { detail: { variant: addedItem } }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    // cc-photo-upload.js restores its ADDING... label on this one.
    document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart: cart } }));
  }

  function photoAddToCart() {
    if (photoBusy) return;
    photoBusy = true;

    // Lets cc-photo-upload.js put the button into its ADDING... state; a 10MB
    // file on 4G is several seconds of silence otherwise.
    document.dispatchEvent(new CustomEvent('cc:photo-submitting'));

    // FormData(form) carries the file as a real Blob part, plus id, quantity and
    // every properties[...] input already in the form. Shopify stores the file
    // and returns the property as a cdn.shopify.com/.../uploads/<hash> URL.
    fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error((body && body.description) || ('add failed (' + res.status + ')'));
          return body;
        });
      })
      .then(function (addedItem) {
        // Re-read the whole cart rather than trusting the add response: cart-level
        // discounts (BOGO and friends) are recalculated across every line, so the
        // authoritative totals only exist on /cart.js after the add.
        return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { announceCart(cart, addedItem); });
      })
      .catch(function (err) {
        warn('photo add-to-cart failed: ' + err.message);
        // Hand the button back so the shopper can retry, but do NOT fire the
        // theme:cart:* events -- nothing was added, and telling the theme the
        // cart changed would refresh the count/drawer against a cart that never
        // moved. cart:update alone is what clears the ADDING... label.
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart: null } }));
      })
      .then(function () { photoBusy = false; });
  }

  if (photo && atc) {
    atc.addEventListener('click', function (e) {
      // No file chosen means nothing to protect -- an optional photo block left
      // empty should go down Pipeline's normal path untouched.
      if (!photoHasFile()) return;
      // Incomplete selections are already stopped by the gate (the button is
      // disabled and the submit guard blocks); don't swallow the event here.
      if (!allGatesPass()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      photoAddToCart();
    }, true);
  }

  // --- native -> pickers ----------------------------------------------------
  // Both blocks expose their own count buttons; reusing them keeps each script's
  // setCount() the single implementation of its locked contract.
  function clickCount(scope, n) {
    if (!scope || !(n > 0)) return;
    var btn = scope.querySelector('[data-cc-count="' + n + '"]');
    if (btn && !btn.classList.contains('is-selected')) btn.click();
  }

  function syncFromNative() {
    if (syncing) return;
    syncing = true;

    if (picker && countOption) clickCount(picker, parseInt(selectedValue(countOption), 10));
    if (engraving && engravingOption) clickCount(engraving, parseInt(selectedValue(engravingOption), 10));
    for (var _g = 0; _g < birthstoneGroups.length; _g++) {
      var _grp = birthstoneGroups[_g];
      if (_grp.option) clickCount(_grp.root, parseInt(selectedValue(_grp.option), 10));
    }
    if (birthflower && birthflowerOption) clickCount(birthflower, parseInt(selectedValue(birthflowerOption), 10));

    if (upgradeToggle && upgradeOption) {
      var val = selectedValue(upgradeOption);
      if (val) {
        var pill = upgradeToggle.querySelector('[data-cc-toggle-option="' + val + '"]');
        if (pill && !pill.classList.contains('is-selected')) pill.click();
      }
    }

    syncing = false;
    gateInitials = readInitialsFromDom();
    gateEngraving = readEngravingFromDom();
    gateBirthstones = readBirthstonesFromDom();
    gateBirthflowers = readBirthflowersFromDom();
    gateZodiac = readZodiacFromDom();
    gateDate = readDateFromDom();
    gateTextFields = readTextFieldsFromDom();
    gatePhoto = readPhotoFromDom();
  }

  // --- pickers -> native ----------------------------------------------------
  document.addEventListener('cc:diamond-changed', function (e) {
    if (syncing || !upgradeOption) return;
    var value = e.detail && e.detail.value;
    if (value) setNativeOption(upgradeOption, value);
  });

  // --- wiring ---------------------------------------------------------------
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.name || t.name.indexOf('options[') !== 0) return;
    syncFromNative();
  });

  if (idInput) {
    var lastId = idInput.value;
    setInterval(function () {
      if (idInput.value === lastId) return;
      lastId = idInput.value;
      syncFromNative();
      applyGate();
    }, 300);
  }

  // Hide each block's own count row when the native selector already owns that
  // question (or when the count has been clamped). Flagged per BLOCK, not on the
  // shared container, so a product with both blocks only hides the driven one.
  if (picker && (countOption || clampToMin)) picker.setAttribute('data-cc-count-from-variant', '');
  if (engraving && engravingOption) engraving.setAttribute('data-cc-count-from-variant', '');
  // Only flag the stone picker when it actually rendered a count row to hide.
  // Fixed-count products (min == max) print their plain "Birthstones" heading
  // through the same __label element, and that heading must survive.
  for (var _g2 = 0; _g2 < birthstoneGroups.length; _g2++) {
    var _g2r = birthstoneGroups[_g2];
    if (_g2r.option && _g2r.root.querySelector('[data-cc-count-selector]')) {
      _g2r.root.setAttribute('data-cc-count-from-variant', '');
    }
  }
  if (birthflower && birthflowerOption && birthflower.querySelector('[data-cc-count-selector]')) {
    birthflower.setAttribute('data-cc-count-from-variant', '');
  }

  // Hide the native upgrade selector only once our toggle is confirmed wired.
  if (upgradeToggle && upgradeOption) {
    var firstUpgradeRadio = radios(upgradeOption)[0];
    if (firstUpgradeRadio) {
      var wrapper = firstUpgradeRadio.closest('.selector-wrapper');
      if (wrapper) wrapper.style.setProperty('display', 'none', 'important');
    }
  }

  if (clampToMin) clickCount(picker, intOr(cfg.initial_picker.min, 1) || 1);

  syncFromNative();
  applyGate();

  // Small diagnostic surface for spot-checking a newly activated batch.
  window.__ccAtelier = {
    productId: data.product_id,
    countOption: countOption,
    engravingOption: engravingOption,
    engravingCountFirst: engravingCountFirst,
    birthstoneOption: birthstoneOption,
    birthstoneGroups: birthstoneGroups.map(function (g) {
      return { key: g.root.getAttribute('data-group-key'), option: g.option };
    }),
    birthflowerOption: birthflowerOption,
    upgradeOption: upgradeOption,
    clampToMin: clampToMin,
    joinSep: window.CC_JOIN_SEP,
    hasPicker: !!picker,
    hasEngraving: !!engraving,
    hasBirthstone: !!birthstone,
    hasBirthflower: !!birthflower,
    hasZodiac: !!zodiac,
    hasDate: !!dateField,
    hasTextFields: !!textFields,
    hasPhoto: !!photo,
    textFieldCount: textFields ? textFields.querySelectorAll('[data-cc-text-field]').length : 0
  };
})();
